import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MagicWand, DownloadSimple, Image as ImageIcon, ShareNetwork } from "@phosphor-icons/react";

const AssetStudio = () => {
  const { user } = useAuth();
  const [type, setType] = useState("logo");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [assets, setAssets] = useState([]);

  useEffect(() => { if (user) api.get("/ai/my-assets").then(r => setAssets(r.data)); }, [user]);

  const generate = async () => {
    if (!user) { toast.error("Connectez-vous"); return; }
    setLoading(true); setCurrent(null);
    try {
      const { data } = await api.post("/ai/generate-image", { prompt: prompt || "Style néon, vibration concert", asset_type: type });
      setCurrent(data);
      setAssets((a) => [{ ...data }, ...a]);
      toast.success("Image générée !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de génération");
    } finally {
      setLoading(false);
    }
  };

  const download = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl; a.download = filename || "xookmedia-asset.png"; a.click();
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#E11D48] font-bold mb-3">Studio IA Nano Banana</p>
        <h1 className="font-display font-black text-4xl tracking-tight mb-2">Logo & Flyer</h1>
        <p className="text-zinc-400 mb-10">Générez l'identité visuelle de votre concert en quelques secondes. Téléchargez et publiez sur tous les réseaux.</p>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6">
            <Tabs value={type} onValueChange={setType}>
              <TabsList data-testid="studio-tabs" className="bg-black/40 border border-white/10">
                <TabsTrigger data-testid="tab-logo" value="logo">Logo</TabsTrigger>
                <TabsTrigger data-testid="tab-flyer" value="flyer">Flyer</TabsTrigger>
              </TabsList>
              <TabsContent value="logo" className="text-sm text-zinc-400 mt-3">
                Logo carré (1:1) parfait pour avatars de réseaux sociaux.
              </TabsContent>
              <TabsContent value="flyer" className="text-sm text-zinc-400 mt-3">
                Flyer vertical (9:16), idéal pour stories Instagram, TikTok.
              </TabsContent>
            </Tabs>

            <label className="text-xs uppercase tracking-widest text-zinc-500 block mt-6 mb-2">Décrivez votre style</label>
            <Textarea
              data-testid="studio-prompt"
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ex: Mon nom 'Nova Lumière', synthwave néon rose, silhouette en lumière..."
              className="bg-black/40 border-white/10 text-white"
            />

            <Button data-testid="studio-generate" disabled={loading} onClick={generate}
              className="w-full mt-4 bg-[#E11D48] hover:bg-[#BE123C]">
              <MagicWand className="mr-2" weight="fill" />
              {loading ? "Génération en cours..." : "Générer avec l'IA"}
            </Button>

            <div className="mt-6 p-3 rounded-lg bg-black/30 border border-white/5 text-xs text-zinc-500 leading-relaxed">
              💡 Astuce: mentionnez votre nom de scène, vos couleurs et l'ambiance souhaitée pour un résultat précis.
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6 flex items-center justify-center min-h-[400px]">
            {loading && (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full border-4 border-[#E11D48]/30 border-t-[#E11D48] animate-spin mb-4" />
                <p className="text-zinc-400 text-sm">L'IA peint votre vision...</p>
              </div>
            )}
            {!loading && current && (
              <div className="w-full">
                <img data-testid="studio-image" src={current.data_url} alt={current.asset_type}
                  className={`w-full rounded-xl ${current.asset_type === "flyer" ? "aspect-[9/16]" : "aspect-square"} object-cover`} />
                <Button data-testid="studio-download" onClick={() => download(current.data_url, `${current.asset_type}.png`)}
                  className="w-full mt-4 bg-white text-black hover:bg-zinc-200">
                  <DownloadSimple className="mr-2" weight="bold" /> Télécharger
                </Button>
              </div>
            )}
            {!loading && !current && (
              <div className="text-center text-zinc-500">
                <ImageIcon size={64} className="mx-auto mb-3 opacity-50" />
                <p>Votre création apparaîtra ici</p>
              </div>
            )}
          </div>
        </div>

        {assets.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display font-bold text-2xl mb-4">Mes créations</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {assets.slice(0, 12).map((a) => (
                <div key={a.id} className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-black">
                  <img src={a.data_url || `/api/ai/assets/${a.filename}`} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetStudio;
